import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, map } from 'rxjs';
import { compareByHubDisplayOrder, CountryService } from '../country/country.service';

/**
 * Story 34.4 AC5 (Codex PR #395 M1) — resolve the bare `/country` route to the
 * FIRST published country instead of a hardcoded `al`. "First" is the product
 * order — `hub_display_order`, the same comparator the pill switcher renders
 * with — so `/country` lands on the leading pill (2026-09-21; was first-by-name
 * under the 34.4 alphabetical decision).
 *
 * The old `redirectTo: 'country/al'` static redirect would strand the default
 * route on an invalid hub the moment Albania is unpublished or removed. This
 * guard reads the published-hub list (the same `GET /countries/published` gate
 * the pill switcher uses) and redirects to the first country in that order.
 *
 * Resilience: `getPublishedCountries()` swallows an outage to `undefined`
 * (the loading state — see CountryService), so on an empty/loading/failed list
 * we fall back to `ch` — the leading pill under the product order, so an
 * outage lands where a healthy load does (was `al`, chosen when Albania led
 * alphabetically; Kazdeda 2026-09-21) — rather than producing a
 * `/country/undefined` URL. The guard returns an `Observable<UrlTree>` (the
 * async sibling of the synchronous `legacyCategoryRedirectGuard`) and is
 * SSR-safe.
 */
export const defaultCountryRedirectGuard: CanActivateFn = (): Observable<UrlTree> => {
  const router = inject(Router);
  const countryService = inject(CountryService);

  return countryService.getPublishedCountries().pipe(
    map((list) => {
      const sorted = (list ?? []).slice().sort(compareByHubDisplayOrder);
      const code = sorted[0]?.iso2_code?.toLowerCase() || 'ch';
      return router.createUrlTree(['/country', code]);
    }),
  );
};
